export type TrainingErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'REVISION_CONFLICT'
  | 'INVARIANT_CONFLICT'
  | 'STORAGE_QUOTA'
  | 'STORAGE_UNAVAILABLE'
  | 'TRANSACTION_ABORTED'
  | 'SCHEMA_UNSUPPORTED'
  | 'UPGRADE_BLOCKED'
  | 'CORRUPTION_DETECTED'
  | 'DATASET_STALE'
  | 'RESTORE_IN_PROGRESS';

export type TrainingFailure = {
  ok: false;
  code: TrainingErrorCode;
};

export type TrainingResult<T> =
  | { ok: true; value: T }
  | TrainingFailure;

export const trainingFailure = (
  code: TrainingErrorCode,
): TrainingFailure => ({ ok: false, code });

export type SessionStatus =
  | 'PLANIFICADA'
  | 'EN_CURSO'
  | 'COMPLETADA'
  | 'INCOMPLETA'
  | 'OMITIDA';

export type ExecutionOutcome =
  | 'COMPLETADA'
  | 'INCOMPLETA';

export interface PlannedSession {
  sessionId: string;
  routineRevisionRef: string;
  cohortLocalDate: string;
  plannedLocalDate: string;
  revision: number;
  explicitOmission: boolean;
}

export interface ExecutedSession {
  executionId: string;
  plannedSessionId: string;
  planRevisionAtStart: number;
  startedInstant: string;
  endedInstant?: string;
  executionOutcome?: ExecutionOutcome;
}

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function validId(value: unknown): value is string {
  return typeof value === 'string' && uuidPattern.test(value);
}

export function validRevision(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

export function validLocalDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export function validPlannedSession(
  session: PlannedSession,
): boolean {
  return !!session
    && validId(session.sessionId)
    && validId(session.routineRevisionRef)
    && validLocalDate(session.cohortLocalDate)
    && validLocalDate(session.plannedLocalDate)
    && validRevision(session.revision)
    && typeof session.explicitOmission === 'boolean';
}

export function validExecutedSession(
  execution: ExecutedSession,
): boolean {
  if (!execution
    || !validId(execution.executionId)
    || !validId(execution.plannedSessionId)
    || !validRevision(execution.planRevisionAtStart)
    || typeof execution.startedInstant !== 'string'
    || Number.isNaN(Date.parse(execution.startedInstant))) {
    return false;
  }

  const hasEnd = execution.endedInstant !== undefined;
  const hasOutcome = execution.executionOutcome !== undefined;

  if (hasEnd !== hasOutcome) return false;

  if (hasEnd
    && (typeof execution.endedInstant !== 'string'
      || Number.isNaN(Date.parse(execution.endedInstant))
      || Date.parse(execution.endedInstant) < Date.parse(execution.startedInstant))) {
    return false;
  }

  if (hasOutcome
    && execution.executionOutcome !== 'COMPLETADA'
    && execution.executionOutcome !== 'INCOMPLETA') {
    return false;
  }

  return true;
}

export function deriveSessionStatus(
  planned: PlannedSession,
  executed?: ExecutedSession,
): TrainingResult<SessionStatus> {
  if (!validPlannedSession(planned)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (executed === undefined) {
    return {
      ok: true,
      value: planned.explicitOmission
        ? 'OMITIDA'
        : 'PLANIFICADA',
    };
  }

  if (!validExecutedSession(executed)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (executed.plannedSessionId !== planned.sessionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (planned.explicitOmission) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (executed.endedInstant === undefined) {
    return {
      ok: true,
      value: 'EN_CURSO',
    };
  }

  return {
    ok: true,
    value: executed.executionOutcome!,
  };
}

export interface OmitPlannedSessionCommand {
  sessionId: string;
  expectedRevision: number;
}

export function omitPlannedSession(
  current: PlannedSession,
  command: OmitPlannedSessionCommand,
  executed?: ExecutedSession,
): TrainingResult<PlannedSession> {
  if (!validPlannedSession(current)
    || !command
    || !validId(command.sessionId)
    || !validRevision(command.expectedRevision)
    || (executed !== undefined && !validExecutedSession(executed))) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (command.sessionId !== current.sessionId
    || current.explicitOmission
    || executed !== undefined
    || !Number.isSafeInteger(current.revision + 1)) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (current.revision !== command.expectedRevision) {
    return trainingFailure('REVISION_CONFLICT');
  }

  return {
    ok: true,
    value: {
      ...current,
      revision: current.revision + 1,
      explicitOmission: true,
    },
  };
}

export interface ReprogramPlannedSessionCommand {
  sessionId: string;
  targetLocalDate: string;
  expectedRevision: number;
}

export interface ReprogrammedPlannedSession {
  status: 'NOOP' | 'CHANGED';
  session: PlannedSession;
}

export function reprogramPlannedSession(
  current: PlannedSession,
  command: ReprogramPlannedSessionCommand,
  destinationOccupied: boolean,
): TrainingResult<ReprogrammedPlannedSession> {
  if (!validPlannedSession(current)
    || !command
    || !validId(command.sessionId)
    || !validLocalDate(command.targetLocalDate)
    || !validRevision(command.expectedRevision)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (current.sessionId !== command.sessionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (current.explicitOmission) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (current.revision !== command.expectedRevision) {
    return trainingFailure('REVISION_CONFLICT');
  }

  if (command.targetLocalDate === current.plannedLocalDate) {
    return {
      ok: true,
      value: {
        status: 'NOOP',
        session: current,
      },
    };
  }

  if (destinationOccupied) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (!Number.isSafeInteger(current.revision + 1)) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  return {
    ok: true,
    value: {
      status: 'CHANGED',
      session: {
        ...current,
        plannedLocalDate: command.targetLocalDate,
        revision: current.revision + 1,
      },
    },
  };
}

export interface StartExecutionCommand {
  executionId: string;
  sessionId: string;
  expectedPlanRevision: number;
  startedInstant: string;
}

export function startPlannedSession(
  planned: PlannedSession,
  command: StartExecutionCommand,
  existingExecutions: readonly ExecutedSession[],
): TrainingResult<ExecutedSession> {
  if (!validPlannedSession(planned)
    || !command
    || !validId(command.executionId)
    || !validId(command.sessionId)
    || !validRevision(command.expectedPlanRevision)
    || typeof command.startedInstant !== 'string'
    || Number.isNaN(Date.parse(command.startedInstant))
    || !Array.isArray(existingExecutions)
    || existingExecutions.some(execution => !validExecutedSession(execution))) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (planned.sessionId !== command.sessionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (planned.explicitOmission) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (planned.revision !== command.expectedPlanRevision) {
    return trainingFailure('REVISION_CONFLICT');
  }

  if (existingExecutions.some(
    execution => execution.executionId === command.executionId,
  )) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (existingExecutions.some(
    execution => execution.plannedSessionId === planned.sessionId,
  )) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (existingExecutions.some(
    execution => execution.endedInstant === undefined,
  )) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  return {
    ok: true,
    value: {
      executionId: command.executionId,
      plannedSessionId: planned.sessionId,
      planRevisionAtStart: planned.revision,
      startedInstant: command.startedInstant,
    },
  };
}

export interface EndExecutionCommand {
  executionId: string;
  endedInstant: string;
}

export type CancelExecutionResult =
  | {
      status: 'RETURN_TO_PLANNED';
      executionId: string;
    }
  | {
      status: 'FINALIZED_INCOMPLETE';
      execution: ExecutedSession;
    };

function validEndCommand(
  command: EndExecutionCommand,
): boolean {
  return !!command
    && validId(command.executionId)
    && typeof command.endedInstant === 'string'
    && !Number.isNaN(Date.parse(command.endedInstant));
}

function validEndAfterStart(
  execution: ExecutedSession,
  endedInstant: string,
): boolean {
  return Date.parse(endedInstant) >= Date.parse(execution.startedInstant);
}

export function completeExecution(
  execution: ExecutedSession,
  command: EndExecutionCommand,
): TrainingResult<ExecutedSession> {
  if (!validExecutedSession(execution) || !validEndCommand(command)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (execution.executionId !== command.executionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (execution.endedInstant !== undefined) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (!validEndAfterStart(execution, command.endedInstant)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  return {
    ok: true,
    value: {
      ...execution,
      endedInstant: command.endedInstant,
      executionOutcome: 'COMPLETADA',
    },
  };
}

export function cancelExecution(
  execution: ExecutedSession,
  command: EndExecutionCommand,
  confirmedWorkingSetCount: number,
): TrainingResult<CancelExecutionResult> {
  if (!validExecutedSession(execution)
    || !validEndCommand(command)
    || !Number.isSafeInteger(confirmedWorkingSetCount)
    || confirmedWorkingSetCount < 0) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (execution.executionId !== command.executionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (execution.endedInstant !== undefined) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (!validEndAfterStart(execution, command.endedInstant)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (confirmedWorkingSetCount === 0) {
    return {
      ok: true,
      value: {
        status: 'RETURN_TO_PLANNED',
        executionId: execution.executionId,
      },
    };
  }

  return {
    ok: true,
    value: {
      status: 'FINALIZED_INCOMPLETE',
      execution: {
        ...execution,
        endedInstant: command.endedInstant,
        executionOutcome: 'INCOMPLETA',
      },
    },
  };
}

export type LoadKind =
  | 'EXTERNA'
  | 'PESO_CORPORAL'
  | 'PESO_CORPORAL_MAS_EXTERNA'
  | 'ASISTIDA';

export type RirValue =
  | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  | '10+'
  | 'DESCONOCIDO';

export interface QuantifiedLoad {
  value: number;
  unit: string;
}

export interface RestValue {
  value: number;
  unit: string;
}

export interface WorkingSet {
  workingSetId: string;
  executedSessionId: string;
  exerciseRevisionRef: string;
  loadKind: LoadKind;
  load?: QuantifiedLoad;
  reps: number;
  rir: RirValue;
  rest?: RestValue;
}

function validExplicitUnit(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function validNonNegativeFinite(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= 0;
}

export function validRir(value: unknown): value is RirValue {
  return value === '10+'
    || value === 'DESCONOCIDO'
    || (
      typeof value === 'number'
      && Number.isInteger(value)
      && value >= 0
      && value <= 9
    );
}

export function validWorkingSet(
  workingSet: WorkingSet,
): boolean {
  if (!workingSet
    || !validId(workingSet.workingSetId)
    || !validId(workingSet.executedSessionId)
    || !validId(workingSet.exerciseRevisionRef)
    || ![
      'EXTERNA',
      'PESO_CORPORAL',
      'PESO_CORPORAL_MAS_EXTERNA',
      'ASISTIDA',
    ].includes(workingSet.loadKind)
    || !Number.isSafeInteger(workingSet.reps)
    || workingSet.reps < 1
    || !validRir(workingSet.rir)) {
    return false;
  }

  if (workingSet.loadKind === 'PESO_CORPORAL') {
    if (workingSet.load !== undefined) return false;
  } else {
    if (workingSet.load === undefined
      || !validNonNegativeFinite(workingSet.load.value)
      || !validExplicitUnit(workingSet.load.unit)) {
      return false;
    }
  }

  if (workingSet.rest !== undefined
    && (
      !validNonNegativeFinite(workingSet.rest.value)
      || !validExplicitUnit(workingSet.rest.unit)
    )) {
    return false;
  }

  return true;
}

export type ExerciseSource =
  | 'LIBRARY'
  | 'CUSTOM';

export interface Exercise {
  exerciseId: string;
  source: ExerciseSource;
  archived: boolean;
  currentRevisionRef: string;
}

export interface LoadStep {
  value: number;
  unit: string;
}

export type ComparisonDimension =
  | { state: 'KNOWN'; value: string }
  | { state: 'NOT_APPLICABLE' }
  | { state: 'UNKNOWN' };

export function validComparisonDimension(
  value: unknown,
): value is ComparisonDimension {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as ComparisonDimension;

  if (candidate.state === 'UNKNOWN'
    || candidate.state === 'NOT_APPLICABLE') {
    return !('value' in candidate);
  }

  return candidate.state === 'KNOWN'
    && typeof candidate.value === 'string'
    && candidate.value.trim().length > 0;
}

export function normalizeComparisonDimension(
  value: ComparisonDimension | undefined,
): ComparisonDimension {
  if (value === undefined || value.state === 'UNKNOWN') {
    return { state: 'UNKNOWN' };
  }

  if (value.state === 'NOT_APPLICABLE') {
    return { state: 'NOT_APPLICABLE' };
  }

  return {
    state: 'KNOWN',
    value: value.value.trim(),
  };
}

export interface ExerciseRevision {
  exerciseRevisionId: string;
  exerciseId: string;
  revision: number;
  name: string;
  muscleGroup: string;
  // Legacy revisions may omit these fields; absence is semantically UNKNOWN.
  variant?: ComparisonDimension;
  equipment?: ComparisonDimension;
  configuration?: ComparisonDimension;
  loadStep?: LoadStep;
}

export interface EditExerciseCommand {
  exerciseRevisionId: string;
  expectedRevision: number;
  name: string;
  muscleGroup: string;
  variant?: ComparisonDimension;
  equipment?: ComparisonDimension;
  configuration?: ComparisonDimension;
  loadStep?: LoadStep;
}

function validNonEmptyText(value: unknown): value is string {
  return typeof value === 'string'
    && value.trim().length > 0;
}

export function validLoadStep(
  value: unknown,
): value is LoadStep {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as LoadStep;

  return typeof candidate.value === 'number'
    && Number.isFinite(candidate.value)
    && candidate.value > 0
    && validExplicitUnit(candidate.unit);
}

export function validExercise(
  exercise: Exercise,
): boolean {
  return !!exercise
    && validId(exercise.exerciseId)
    && (
      exercise.source === 'LIBRARY'
      || exercise.source === 'CUSTOM'
    )
    && typeof exercise.archived === 'boolean'
    && validId(exercise.currentRevisionRef);
}

export function validExerciseRevision(
  revision: ExerciseRevision,
): boolean {
  return !!revision
    && validId(revision.exerciseRevisionId)
    && validId(revision.exerciseId)
    && validRevision(revision.revision)
    && validNonEmptyText(revision.name)
    && validNonEmptyText(revision.muscleGroup)
    && (revision.variant === undefined
      || validComparisonDimension(revision.variant))
    && (revision.equipment === undefined
      || validComparisonDimension(revision.equipment))
    && (revision.configuration === undefined
      || validComparisonDimension(revision.configuration))
    && (
      revision.loadStep === undefined
      || validLoadStep(revision.loadStep)
    );
}

export function editExercise(
  exercise: Exercise,
  currentRevision: ExerciseRevision,
  command: EditExerciseCommand,
): TrainingResult<{
  exercise: Exercise;
  revision: ExerciseRevision;
}> {
  if (!validExercise(exercise)
    || !validExerciseRevision(currentRevision)
    || !command
    || !validId(command.exerciseRevisionId)
    || !validRevision(command.expectedRevision)
    || !validNonEmptyText(command.name)
    || !validNonEmptyText(command.muscleGroup)
    || (command.variant !== undefined
      && !validComparisonDimension(command.variant))
    || (command.equipment !== undefined
      && !validComparisonDimension(command.equipment))
    || (command.configuration !== undefined
      && !validComparisonDimension(command.configuration))
    || (
      command.loadStep !== undefined
      && !validLoadStep(command.loadStep)
    )) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (exercise.archived) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (currentRevision.exerciseId !== exercise.exerciseId
    || exercise.currentRevisionRef !== currentRevision.exerciseRevisionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (currentRevision.revision !== command.expectedRevision) {
    return trainingFailure('REVISION_CONFLICT');
  }

  if (command.exerciseRevisionId === currentRevision.exerciseRevisionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (!Number.isSafeInteger(currentRevision.revision + 1)) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  const nextRevision: ExerciseRevision = {
    exerciseRevisionId: command.exerciseRevisionId,
    exerciseId: exercise.exerciseId,
    revision: currentRevision.revision + 1,
    name: command.name.trim(),
    muscleGroup: command.muscleGroup.trim(),
    variant: normalizeComparisonDimension(
      command.variant ?? currentRevision.variant,
    ),
    equipment: normalizeComparisonDimension(
      command.equipment ?? currentRevision.equipment,
    ),
    configuration: normalizeComparisonDimension(
      command.configuration ?? currentRevision.configuration,
    ),
    ...(command.loadStep === undefined
      ? {}
      : {
          loadStep: {
            value: command.loadStep.value,
            unit: command.loadStep.unit.trim(),
          },
        }),
  };

  return {
    ok: true,
    value: {
      exercise: {
        ...exercise,
        currentRevisionRef: nextRevision.exerciseRevisionId,
      },
      revision: nextRevision,
    },
  };
}

export function archiveExercise(
  exercise: Exercise,
): TrainingResult<Exercise> {
  if (!validExercise(exercise)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (exercise.archived) {
    return {
      ok: true,
      value: exercise,
    };
  }

  return {
    ok: true,
    value: {
      ...exercise,
      archived: true,
    },
  };
}

export interface Routine {
  routineId: string;
  archived: boolean;
  currentRevisionRef: string;
}

export interface RoutineExerciseRef {
  exerciseRevisionRef: string;
  order: number;
}

export interface RoutineRevision {
  routineRevisionId: string;
  routineId: string;
  revision: number;
  name: string;
  exercises: readonly RoutineExerciseRef[];
}

export interface EditRoutineCommand {
  routineRevisionId: string;
  expectedRevision: number;
  name: string;
  exercises: readonly RoutineExerciseRef[];
}

function validRoutineExerciseRef(
  value: RoutineExerciseRef,
): boolean {
  return !!value
    && validId(value.exerciseRevisionRef)
    && Number.isSafeInteger(value.order)
    && value.order >= 0;
}

function validRoutineExerciseRefs(
  values: readonly RoutineExerciseRef[],
): boolean {
  if (!Array.isArray(values)) return false;
  if (values.some(value => !validRoutineExerciseRef(value))) return false;

  const orders = values.map(value => value.order);
  const refs = values.map(value => value.exerciseRevisionRef);

  return new Set(orders).size === orders.length
    && new Set(refs).size === refs.length;
}

export function validRoutine(
  routine: Routine,
): boolean {
  return !!routine
    && validId(routine.routineId)
    && typeof routine.archived === 'boolean'
    && validId(routine.currentRevisionRef);
}

export function validRoutineRevision(
  revision: RoutineRevision,
): boolean {
  return !!revision
    && validId(revision.routineRevisionId)
    && validId(revision.routineId)
    && validRevision(revision.revision)
    && validNonEmptyText(revision.name)
    && validRoutineExerciseRefs(revision.exercises);
}

export function editRoutine(
  routine: Routine,
  currentRevision: RoutineRevision,
  command: EditRoutineCommand,
): TrainingResult<{
  routine: Routine;
  revision: RoutineRevision;
}> {
  if (!validRoutine(routine)
    || !validRoutineRevision(currentRevision)
    || !command
    || !validId(command.routineRevisionId)
    || !validRevision(command.expectedRevision)
    || !validNonEmptyText(command.name)
    || !validRoutineExerciseRefs(command.exercises)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (routine.archived) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (currentRevision.routineId !== routine.routineId
    || routine.currentRevisionRef !== currentRevision.routineRevisionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (currentRevision.revision !== command.expectedRevision) {
    return trainingFailure('REVISION_CONFLICT');
  }

  if (command.routineRevisionId === currentRevision.routineRevisionId) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  if (!Number.isSafeInteger(currentRevision.revision + 1)) {
    return trainingFailure('INVARIANT_CONFLICT');
  }

  const nextRevision: RoutineRevision = {
    routineRevisionId: command.routineRevisionId,
    routineId: routine.routineId,
    revision: currentRevision.revision + 1,
    name: command.name.trim(),
    exercises: command.exercises.map(item => ({
      exerciseRevisionRef: item.exerciseRevisionRef,
      order: item.order,
    })),
  };

  return {
    ok: true,
    value: {
      routine: {
        ...routine,
        currentRevisionRef: nextRevision.routineRevisionId,
      },
      revision: nextRevision,
    },
  };
}

export function archiveRoutine(
  routine: Routine,
): TrainingResult<Routine> {
  if (!validRoutine(routine)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  if (routine.archived) {
    return {
      ok: true,
      value: routine,
    };
  }

  return {
    ok: true,
    value: {
      ...routine,
      archived: true,
    },
  };
}
